import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSWRConfig } from 'swr';
import api from '../../../services/api';
import { APP_CONFIG } from '../../../config/appConfig';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { CONVERSATIONS_KEY } from '../../ai-chat/useAiChat';
import { formatEmailAssistantPromptMessage } from '../../../lib/formatAiMarkdown';
import {
  buildEmailChatHistoryKey,
  useEmailAiChatHistory,
  useEmailAiModels,
  useEmailAiPrompts,
} from '../hooks/useEmailAiDrawerData';
import { formatAiRequestError } from '../utils/formatAiRequestError';
import { useAiAssistantEnabled, useAiEmailsEnabled } from '../../ai/useAiAssistant';
import type {
  EmailAiAssistantContext,
  EmailAiAssistantController,
} from '../components/ai-assistant/emailAiAssistant.types';
import {
  historyToThread,
  isValidEmailAiThreadId,
  newEmailAiMessageId,
  plainTextToHtml,
} from '../components/ai-assistant/emailAiAssistant.utils';
import type { EmailAiThreadMessage } from '../components/EmailAiConversation';

export interface UseEmailAiAssistantOptions {
  open: boolean;
  context: EmailAiAssistantContext;
  subject: string;
  body: string;
  getBody?: () => string;
  selectedText?: string;
  getSelectedText?: () => string;
  onClose: () => void;
  onReplaceBody?: (html: string) => void;
  onInsertAtCursor?: (html: string) => void;
  onBumpEditorKey?: () => void;
  emailId?: number;
  partitionAt?: string | null;
  clientId?: number;
  composeSessionId?: string;
}

export function useEmailAiAssistant(options: UseEmailAiAssistantOptions): EmailAiAssistantController {
  const {
    open,
    context,
    subject,
    body,
    getBody,
    selectedText: selectedTextProp = '',
    getSelectedText,
    onClose,
    onReplaceBody,
    onInsertAtCursor,
    onBumpEditorKey,
    emailId,
    partitionAt,
    clientId,
    composeSessionId,
  } = options;

  const { showSuccess, showError } = useNotification();
  const { can } = useAuth();
  const aiEmailsEnabled = useAiEmailsEnabled();
  const aiAssistantEnabled = useAiAssistantEnabled();
  const canOpenAiChat = aiAssistantEnabled && can('dashboard', 'view');
  const { mutate: globalMutate } = useSWRConfig();

  const abortRef = useRef<AbortController | null>(null);
  const hydratedKeyRef = useRef<string | null>(null);

  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [additionalText, setAdditionalText] = useState('');
  const [loading, setLoading] = useState(false);
  const [runningPromptName, setRunningPromptName] = useState<string | undefined>();
  const [thread, setThread] = useState<EmailAiThreadMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [linkedChatId, setLinkedChatId] = useState<number | null>(null);

  const isRead = context === 'read';
  const canEditDraft = Boolean(onReplaceBody && onInsertAtCursor);
  const menuContext = isRead ? 'read' : 'compose';

  const historyParams = useMemo(
    () => ({ emailId, partitionAt, composeSessionId }),
    [emailId, partitionAt, composeSessionId],
  );

  const { data: prompts = [], error: promptsSwError, isLoading: loadingPrompts } = useEmailAiPrompts(
    menuContext,
    open && aiEmailsEnabled,
  );
  const { data: models = [], isLoading: loadingModels } = useEmailAiModels(open && aiEmailsEnabled);
  const { data: chatHistory, isLoading: loadingHistory } = useEmailAiChatHistory(
    historyParams,
    open && aiEmailsEnabled,
  );

  const promptsError = promptsSwError
    ? formatAiRequestError(promptsSwError, 'Could not load prompts')
    : null;
  const drawerLoadingMeta = loadingPrompts || loadingModels;

  const getCurrentSelectedText = useCallback(
    () => (getSelectedText?.()?.trim() ?? selectedTextProp?.trim() ?? ''),
    [getSelectedText, selectedTextProp],
  );
  const selectedLen = getCurrentSelectedText().length;
  const selectedPrompt = prompts.find((p) => p.id === selectedPromptId);
  const needAdditional = selectedPrompt?.needAdditionalText ?? false;
  const needSelected = selectedPrompt?.needSelectedText ?? false;
  const canRun = !needSelected || selectedLen > 0;

  const getCurrentBody = useCallback(() => (getBody?.() ?? body ?? '').trim(), [getBody, body]);

  useEffect(() => {
    if (!open || !aiEmailsEnabled) return;
    if (selectedPromptId && prompts.some((p) => p.id === selectedPromptId)) return;
    setSelectedPromptId(prompts[0]?.id ?? null);
  }, [open, aiEmailsEnabled, prompts, selectedPromptId]);

  useEffect(() => {
    if (!open || models.length === 0) return;
    setSelectedModelId((prev) => {
      if (prev && models.some((m) => m.id === prev)) return prev;
      const def = models.find((m) => m.defaultModel) ?? models[0];
      return def?.id ?? '';
    });
  }, [open, models]);

  useEffect(() => {
    if (!open || !aiEmailsEnabled) {
      hydratedKeyRef.current = null;
      return;
    }
    if (loadingHistory) return;

    const historyKey = JSON.stringify(historyParams);
    if (hydratedKeyRef.current === historyKey) return;
    hydratedKeyRef.current = historyKey;

    setThread(historyToThread(chatHistory?.messages ?? []));
    setLinkedChatId(chatHistory?.conversation?.id ?? null);
    setChatInput('');
    setAdditionalText('');
    setRunningPromptName(undefined);
  }, [open, aiEmailsEnabled, loadingHistory, chatHistory, historyParams]);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
    }
  }, [open]);

  const cancelInFlight = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setRunningPromptName(undefined);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (loading) {
          e.preventDefault();
          cancelInFlight();
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, loading, cancelInFlight]);

  const beginAbortableRequest = useCallback(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return controller.signal;
  }, []);

  const buildContextPayload = useCallback(
    () => ({
      subject: subject || undefined,
      bodyHtml: getCurrentBody(),
      selectedText: getCurrentSelectedText() || undefined,
      model: selectedModelId.trim() || undefined,
      emailId: emailId ?? undefined,
      partitionAt: partitionAt ?? undefined,
      composeSessionId: composeSessionId?.trim() || undefined,
    }),
    [subject, getCurrentBody, getCurrentSelectedText, selectedModelId, emailId, partitionAt, composeSessionId],
  );

  const buildPromptPayload = useCallback(
    () => ({
      promptId: selectedPromptId != null ? String(selectedPromptId) : undefined,
      promptName: selectedPrompt?.name,
      customText: needAdditional ? additionalText : undefined,
      ...buildContextPayload(),
    }),
    [selectedPromptId, selectedPrompt?.name, needAdditional, additionalText, buildContextPayload],
  );

  const toApiMessages = useCallback(
    (messages: EmailAiThreadMessage[]) => messages.map((m) => ({ role: m.role, content: m.content })),
    [],
  );

  const handleRunServerAi = useCallback(async () => {
    if (!selectedPromptId) {
      showError('Select a prompt.');
      return;
    }
    if (needAdditional && !additionalText.trim()) {
      showError('Enter additional text for this prompt.');
      return;
    }

    const payload = buildPromptPayload();
    const signal = beginAbortableRequest();
    setLoading(true);
    setRunningPromptName(selectedPrompt?.name);

    try {
      const { data } = await api.post<{ answer: string; promptText?: string }>(
        '/ai/email/run-prompt',
        payload,
        { timeout: APP_CONFIG.api.aiAssistantTimeout, signal },
      );

      const userContent = formatEmailAssistantPromptMessage(
        selectedPrompt?.name,
        subject,
        emailId,
        data.promptText ?? '',
        composeSessionId,
      );
      setThread((prev) => [
        ...prev,
        { id: newEmailAiMessageId(), role: 'user', content: userContent },
        { id: newEmailAiMessageId(), role: 'assistant', content: data?.answer ?? '' },
      ]);
      void globalMutate(CONVERSATIONS_KEY);
      void globalMutate(buildEmailChatHistoryKey(historyParams)).then((result) => {
        const conv = (result as { conversation?: { id?: number } } | undefined)?.conversation;
        if (conv?.id) setLinkedChatId(conv.id);
      });
    } catch (err: unknown) {
      const msg = formatAiRequestError(err);
      if (msg !== 'Request cancelled') showError(msg);
    } finally {
      if (abortRef.current?.signal === signal) abortRef.current = null;
      setLoading(false);
      setRunningPromptName(undefined);
    }
  }, [
    buildPromptPayload,
    beginAbortableRequest,
    selectedPromptId,
    selectedPrompt?.name,
    needAdditional,
    additionalText,
    subject,
    emailId,
    composeSessionId,
    showError,
    globalMutate,
    historyParams,
  ]);

  const handleSendMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || loading) return;

    const userMsg: EmailAiThreadMessage = { id: newEmailAiMessageId(), role: 'user', content: text };
    const nextThread = [...thread, userMsg];
    setThread(nextThread);
    setChatInput('');
    const signal = beginAbortableRequest();
    setLoading(true);
    setRunningPromptName(undefined);

    try {
      const { data } = await api.post<{ answer: string }>(
        '/ai/email/chat',
        { messages: toApiMessages(nextThread), ...buildContextPayload() },
        { timeout: APP_CONFIG.api.aiAssistantTimeout, signal },
      );
      setThread((prev) => [
        ...prev,
        { id: newEmailAiMessageId(), role: 'assistant', content: data?.answer ?? '' },
      ]);
      void globalMutate(CONVERSATIONS_KEY);
      void globalMutate(buildEmailChatHistoryKey(historyParams)).then((result) => {
        const conv = (result as { conversation?: { id?: number } } | undefined)?.conversation;
        if (conv?.id) setLinkedChatId(conv.id);
      });
    } catch (err: unknown) {
      setThread((prev) => prev.filter((m) => m.id !== userMsg.id));
      setChatInput(text);
      const msg = formatAiRequestError(err);
      if (msg !== 'Request cancelled') showError(msg);
    } finally {
      if (abortRef.current?.signal === signal) abortRef.current = null;
      setLoading(false);
    }
  }, [
    chatInput,
    loading,
    thread,
    toApiMessages,
    buildContextPayload,
    beginAbortableRequest,
    showError,
    globalMutate,
    historyParams,
  ]);

  const handleInsertAtCursor = useCallback(
    (text: string) => {
      if (!onInsertAtCursor) return;
      const html = text.includes('<') ? text : plainTextToHtml(text);
      onInsertAtCursor(html);
      showSuccess('Inserted at cursor.');
    },
    [onInsertAtCursor, showSuccess],
  );

  const handleReplaceBody = useCallback(
    (text: string) => {
      if (!onReplaceBody) return;
      const html = text.includes('<') ? text : plainTextToHtml(text);
      onReplaceBody(html);
      onBumpEditorKey?.();
      showSuccess('Draft replaced.');
    },
    [onReplaceBody, onBumpEditorKey, showSuccess],
  );

  const recordingNote =
    aiEmailsEnabled && !aiAssistantEnabled
      ? 'Conversations are saved. Enable AI assistant (CRM & dashboard) in Settings to open them in AI Chat.'
      : aiEmailsEnabled && !can('dashboard', 'view')
        ? 'Conversations are saved. You need Dashboard access to open them in AI Chat.'
        : null;

  const openInAiChatHref =
    linkedChatId && canOpenAiChat ? `/ai-chat?id=${linkedChatId}` : null;

  return {
    aiEmailsEnabled,
    isRead,
    canEditDraft,
    canOpenAiChat,
    openInAiChatHref,
    recordingNote,
    prompts,
    models,
    promptsError,
    drawerLoadingMeta,
    loadingHistory,
    loading,
    runningPromptName,
    cancelInFlight,
    selectedModelId,
    setSelectedModelId,
    selectedPromptId,
    setSelectedPromptId,
    needAdditional,
    needSelected,
    canRun,
    additionalText,
    setAdditionalText,
    selectedPromptCode: selectedPrompt?.code,
    thread,
    chatInput,
    setChatInput,
    handleRunServerAi: () => void handleRunServerAi(),
    handleSendMessage: () => void handleSendMessage(),
    clearThread: () => {
      setThread([]);
      setLinkedChatId(null);
    },
    handleInsertAtCursor,
    handleReplaceBody,
    selectedLen,
    showSmartReplies: isValidEmailAiThreadId(emailId),
    emailId,
    partitionAt,
    clientId,
  };
}
